import React, { useState } from 'react';
import Chat from './Chat';
import type { ChatMessage } from './ChatService';
import type { AcsChatConfig } from './AcsChatConfig';
import { useAcsChat } from './useAcsChat';
import { Box } from '@mui/material';
import CustomButton from '../lib/ui-components/Button/CustomButton';
import CustomTypography from '../lib/ui-components/Typography/CustomTypography';
import { getPortalIcon } from './PortalIcons';

// ============================================================================
// ChatExample – ready-to-use ACS group chat component
// ============================================================================
//
// USAGE IN ANY PORTAL (clinical / facility / operations):
//
//   // With ACS (real group chat):
//   <ChatExample
//     onClose={() => setIsChatOpen(false)}
//     acsConfig={{
//       endpoint: import.meta.env.VITE_ACS_ENDPOINT,
//       token: userAcsToken,          // from your backend
//       userId: userAcsId,            // from your backend
//       displayName: 'Dr. Smith',
//     }}
//     threadId={consult.chatThreadId} // from your backend
//   />
//
//   // Without ACS (offline / mock mode – uses local messages):
//   <ChatExample onClose={() => setIsChatOpen(false)} />
//
// ============================================================================

interface ChatQLERProps {
  onClose?: () => void;
  acsConfig?: AcsChatConfig;
  threadId?: string;
  title?: string;
  subtitle?: string;
  isViewOnly?: boolean;
  onNewMessage?: (message: ChatMessage) => void;
  hidden?: boolean;
}

// ---------------------------------------------------------------------------
// Mock data for offline / development mode
// ---------------------------------------------------------------------------

const ChatQLER: React.FC<ChatQLERProps> = ({
  onClose,
  acsConfig,
  threadId,
  title = 'Chat',
  subtitle = '',
  isViewOnly = false,
  onNewMessage,
  hidden = false,
}) => {
  // -------------------------------------------------------------------------
  // ACS mode: real group chat across portals
  // -------------------------------------------------------------------------
  const isAcsMode = Boolean(acsConfig?.endpoint && acsConfig?.token);
  const isWaitingForData =
    acsConfig !== undefined && (!threadId || !acsConfig.token);

  // Always call hooks unconditionally (Rules of Hooks)
  const acs = useAcsChat({
    config: acsConfig || {
      endpoint: '',
      token: '',
      userId: '',
      displayName: '',
    },
    threadId: threadId || '',
    autoConnect: isAcsMode && !isWaitingForData,
    onNewMessage,
  });

  // -------------------------------------------------------------------------
  // Mock mode: local-only messages (for dev / offline)
  // -------------------------------------------------------------------------
  const [mockMessages, setMockMessages] = useState<ChatMessage[]>([]);

  const handleMockSend = (message: string) => {
    const newMessage: ChatMessage = {
      id: Date.now().toString(),
      senderId: 'currentUser',
      senderName: 'You',
      senderInitials: 'OP',
      message: message,
      timestamp: new Date(),
      isCurrentUser: true,
    };
    setMockMessages((prev) => [...prev, newMessage]);
  };

  // -------------------------------------------------------------------------
  // Pick the right data source
  // -------------------------------------------------------------------------
  const messages = isAcsMode ? acs.messages : mockMessages;
  const currentUserId = isAcsMode ? acs.currentUserId : 'currentUser';

  const handleSendMessage = (text: string) => {
    if (isAcsMode) {
      acs.sendMessage(text);
    } else {
      handleMockSend(text);
    }
  };

  // -------------------------------------------------------------------------
  // Loading / Error states
  // -------------------------------------------------------------------------
  // Show loading when waiting for data OR when ACS is connecting (but only if chat is visible)
  if (!hidden && (isWaitingForData || (isAcsMode && acs.isLoading))) {
    return (
      <Box className="display-loader-center">
        <CustomTypography id="chat-loading" variant="h6">
          Loading Chat...
        </CustomTypography>
      </Box>
    );
  }

  if (!hidden && isAcsMode && acs.error) {
    const isForbiddenError =
      acs.error.toLowerCase().includes('forbidden') ||
      acs.error.toLowerCase().includes('permission');

    return (
      <Box className="display-loader-center chat-error">
        <CustomTypography id="chat-error" variant="body3" color="error">
          {isForbiddenError
            ? 'Unable to load chat. Please try refreshing.'
            : acs.error}
        </CustomTypography>
        <CustomButton
          id="chat-refresh-button"
          variant="contained"
          color="primary"
          onClick={() => globalThis.location.reload()}
          className="chat-refresh-button"
        >
          Refresh Chat
        </CustomButton>
      </Box>
    );
  }

  return (
    <Box className={`chat-height ${hidden ? 'chat-hidden' : 'chat-visible'}`}>
      <Chat
        title={title}
        subtitle={subtitle}
        messages={messages}
        currentUserId={currentUserId}
        onSendMessage={handleSendMessage}
        onClose={onClose}
        placeholder="Type your message"
        isViewOnly={isViewOnly}
        getAvatarIcon={getPortalIcon}
      />
    </Box>
  );
};

export default ChatQLER;
